import { Method } from '../internal';
import { LinkArray } from './links';

/**
 * Useful when using a plain JavaScript object as a map of values of a given type.
 */
export type Dict<T> = Record<string, T | undefined>;

/**
 * TypeScript's Readonly<T> type is not recursive, meaning any objects residing in
 * the root level of the type can still be modified.  RecursivelyReadonly provides
 * an equivalent Readonly<T> type on every level of the object.
 */
export type RecursivelyReadonly<T> = {
  readonly [Key in keyof T]: RecursivelyReadonly<T[Key]>;
} &
  (T extends (...args: any) => unknown ? (...params: Parameters<T>) => ReturnType<T> : unknown);

// Passing Rels as union of string literals constrains possible rel values
// a.k.a. LinkType<'self' | 'create'>
export interface LinkType<Rels extends string = string> {
  method: Method;
  rel: Rels;
  href: string;
  uri: string;
  type?: string;
  itemType?: string;
  responseType?: string;
  responseItemType?: string;
}

interface BaseType {
  version?: number;
  links?: LinkType[];
}

export interface RootLinksType extends BaseType {}

export interface ErrorType {
  version: number;
  httpStatusCode: number;
  errorCode: number;
  message: string;
  details: string[];
}

export interface CollectionType<T> extends BaseType {
  accept?: string;
  count?: number;
  start?: number;
  limit?: number;
  name?: string;
  items: T[];
}

export interface PreferenceType extends BaseType {
  id: string;
  value: string;
  application?: string;
}

export interface AddressType {
  type: string;
  locality: string;
  country: string;
  postalCode: string;
}

export interface IdentityType extends BaseType {
  id: string;
  name?: string;
  displayName?: string;
  type?: UserType;
  providerId?: string;
  creationTimeStamp?: Date;
  modifiedTimeStamp?: Date;
  state?: string;
  addresses?: AddressType[];
}

export interface IdentityAvatarType extends BaseType {
  default?: boolean;
  name?: string;
  type?: string;
  creationTimeStamp?: Date;
  modifiedTimeStamp?: Date;
}

export interface AnnotationType extends BaseType {
  id: string;
  name: string;
  domain: string;
  label?: string;
  description?: string;
  createdBy?: string;
  creationTimeStamp?: Date;
  modifiedBy?: string;
  modifiedTimeStamp?: Date;
}

export interface AnnotationMemberType extends BaseType {
  id: string;
  annotationId: string;
  resourceUri?: string;
  resourceType?: string;
  value?: string;
  createdBy?: string;
  creationTimeStamp?: Date;
  modifiedBy?: string;
  modifiedTimeStamp?: Date;
}

export interface FolderType extends BaseType {
  id: string;
  name?: string;
  type?: string;
  description?: string;
  parentFolderUri?: string;
  iconUri?: string;
  memberCount?: number;
  properties?: Map<string, string>;
  createdBy?: string;
  creationTimeStamp?: Date;
  modifiedBy?: string;
  modifiedTimeStamp?: Date;
}

export interface NotificationType extends BaseType {
  id: string;
  name?: string;
  subject?: string;
  type?: string;
  timeStamp?: string;
  publishTimeStamp?: string;
  unread?: boolean;
  read?: boolean;
  message?: string;
  category?: string;
  level?: string;
  recipientId?: string;
  userId?: string;
}
export interface CopyrightType extends BaseType {
  developmentStartYear?: string;
  developmentEndYear?: string;
}

export interface SiteInfoType extends BaseType {
  name?: string;
  id?: string;
}

export interface FileType {
  creationTimeStamp: string;
  modifiedTimeStamp: string;
  createdBy: string;
  modifiedBy: string;
  id: string;
  parentUri?: string;
  properties: object;
  contentType: string;
  documentType?: string;
  encoding: string;
  links: Array<object>;
  name: string;
  size: number;
  searchable: boolean;
  fileStatus: string;
  fileVersion: number;
  version: number;
}

export type ResourceType = 'id' | 'uri' | 'mixed';

export type FilterOperator = 'contains' | 'eq' | 'in' | 'startsWith';

export type LogicalOperator = 'and' | 'or';

export type UserType = 'user' | 'group';

export interface BaseThemeType extends BaseType {
  id: string;
  contentType?: string;
  createdFrameworkVersion?: string;
  defaultTheme?: boolean;
  hidden?: boolean;
  lastModifiedTimeStamp?: string;
  published?: boolean;
  sourceModifiedTimeStamp?: string;
  styleData?: { [key: string]: string };
  updatedFrameworkVersion?: string;
  rootUrl?: string;
}

export interface SystemThemeType extends BaseThemeType {
  type: 'system';
}

export interface CustomThemeType extends BaseThemeType {
  type: 'custom';
  name: string;
  baseThemeId?: string;
}

export function isSystemThemeType(
  theme: SystemThemeType | CustomThemeType
): theme is SystemThemeType {
  return theme.type === 'system';
}

export type ThemeType = SystemThemeType | CustomThemeType;

export type AppliedThemeType = Pick<SystemThemeType, 'id' | 'version' | 'links'>;

export interface CommentType extends BaseType {
  id?: string;
  resourceUri?: string;
  subject?: string;
  text?: string;
  elementId?: string;
  parentId?: string;
  createdBy?: string;
  modifiedBy?: string;
  creationTimeStamp?: string;
  modifiedTimeStamp?: string;
  priority?: string;
  index?: number;
  childCount?: number;
  searchCount?: number;
  authorDisplayName?: string;
  newFiles?: Array<File>;
  deletedFiles?: Array<AttachmentType>;
  attachedFiles?: Array<AttachmentType>;
  replies?: Array<CommentType>;
  isDummyComment?: boolean;
}

export interface AttachmentType extends BaseType, File {
  id: string;
  name: string;
  parentUri: string;
  contentType: string;
  documentType: string;
  quarantine: boolean;
}

export interface FileRequestType {
  selection: {
    version: number;
    type: string;
    template: string;
    resources: {
      id: string[];
    };
  };
}

export interface CommentRequestType {
  start?: number;
  limit?: number;
  sortBy?: string;
  filter?: string;
  resourceUri?: string;
}

export interface ActivityType extends BaseType {
  uri: string;
  type: string;
  application: string;
  objectType: string;
  id?: string;
  count?: number;
  label?: number;
  timeStamp?: string;
  userId?: string;
  userName?: string;
  values?: string[];
  associatedUris?: string[];
  properties?: Map<string, string>;
}

export interface ActivityObjectType extends BaseType {
  createdBy: string;
  creationTimeStamp: string;
  description: string;
  id: string;
  modifiedBy: string;
  modifiedTimeStamp: string;
  name: string;
  type: string;
}

/**
 * Possible states on a received job response
 */
export type JobState =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completedWithErrors'
  | 'failed'
  | 'canceling'
  | 'canceled'
  | 'skipped';

/**
 * Base type for a jobs pattern service return object. Outside of these properties individual APIs
 * could have anything else on them, but at least these properties should be present.
 */
export interface BaseJob {
  id: string;
  version: number;
  state: JobState;
  // I'd like to constrain links to LinkArrays that contain the 'self' rel, but have not be able to
  // find the right TypeScript magic. So, extenders of BaseJob must include a 'self' rel in order to
  // actually work.
  links: LinkArray;
}

/**
 * A function that creates a job (usually meaning a POST to an endpoint), and returns a promise that
 * resolves to the created job.
 */
export type CreateJobFn<Job extends BaseJob = BaseJob> = (
  abortController?: AbortController
) => Promise<Job>;
export type CreateJob<Job extends BaseJob = BaseJob> = () => Promise<Job>;

export interface UseJobConfig<Job extends BaseJob = BaseJob> {
  // These must be optional since hook consumers might not have what is needed to generate these on
  // first render
  createJobFn?: CreateJobFn<Job>;
  jobHash?: string;
  deferJobCreation?: boolean;
  disableUpdateOnCompletion?: boolean;
}

export interface UseJobReturn<Job extends BaseJob = BaseJob> {
  job?: Job;
  isAwaitingInitialResponse: boolean;
  createJob?: CreateJob<Job>;
  registerJobCallback?: () => void;
}

export type PollingResultsCallback<Job extends BaseJob = BaseJob> = (pollingResults: Job) => void;

/**
 * Representation of the request object for creating a thumbnails service job
 */
export interface ThumbnailRequest {
  acceptItem: string;
  resourceUri: string;
  resourceType?: string;
  detailLevel?: 'low' | 'high';
  scope?: 'element' | 'page';
  returnMultiple?: boolean;
  refresh?: boolean;
  sizingOptions?: {
    viewWidth?: number;
    viewHeight?: number;
    devicePixelRatio?: number;
    maxTextCharacters?: number;
    maxTableColumns?: number;
    maxTableRows?: number;
  };
  accessibilityOptions?: {
    highContrast?: boolean;
  };
  themeId?: string;
  pagingOptions?: {
    start: number;
    limit: number;
  };
  version?: number;
}

/**
 * Representation of a thumbnails service job object
 */
export interface ThumbnailJob extends BaseJob {
  thumbnails?: Thumbnail[];
  error?: ThumbnailError;
  duration?: number;
  defaultThumbnailSasThemesIconKey?: string;
  pagingState?: {
    start: number;
    limit: number;
    count: number;
  };
  links: LinkArray<'defaultThumbnailImage' | 'self'>;
}

/**
 * Representation of an individual thumbnail result
 */
export interface Thumbnail {
  state: JobState;
  error?: ThumbnailError;
  modifiedTimeStamp?: Date;
  links?: LinkArray<'thumbnail' | 'staleThumbnail'>;
}

/**
 * Representation of a thumbnails service error object
 */
export interface ThumbnailError {
  message?: string;
  id?: string;
  errorCode?: number;
  httpStatusCode?: number;
  details?: string[];
  remediation?: string;
  errors?: ThumbnailError[];
  links?: LinkArray;
  version?: number;
}

export type AuthorizationPermissionType = 'read' | 'update' | 'delete' | 'secure';

export type AuthorizationType = 'grant' | 'prohibit';
